import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowDownIcon, ChevronRightIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
type Bi = {
  fr: string;
  en: string;
};
const OUTCOMES: {
  key: string;
  stat: Bi;
  label: Bi;
}[] = [{
  key: 'leak',
  stat: {
    fr: '89 %',
    en: '89%'
  },
  label: {
    fr: 'Potentiel de réduction des fuites',
    en: 'Leak reduction potential'
  }
}, {
  key: 'repair',
  stat: {
    fr: '34 %',
    en: '34%'
  },
  label: {
    fr: 'Réduction du délai de réparation',
    en: 'Shorter repair time'
  }
}, {
  key: 'usage',
  stat: {
    fr: '5 à 15 %',
    en: '5–15%'
  },
  label: {
    fr: 'Réduction de la consommation',
    en: 'Consumption reduction'
  }
}];
const ENVIRONMENT: {
  key: string;
  label: Bi;
}[] = [{
  key: 'deployment',
  label: {
    fr: 'Déploiement des Compteurs d’eau ATNA',
    en: 'ATNA Smart Water deployment'
  }
}, {
  key: 'identity',
  label: {
    fr: 'Infrastructure d’identité vérifiée',
    en: 'Verified identity infrastructure'
  }
}, {
  key: 'leaks',
  label: {
    fr: 'Flux de détection des fuites',
    en: 'Leak detection workflows'
  }
}, {
  key: 'law25',
  label: {
    fr: 'Conforme à la Loi 25',
    en: 'Law 25 ready'
  }
}, {
  key: 'hosting',
  label: {
    fr: 'Hébergé au Canada',
    en: 'Canadian hosted'
  }
}, {
  key: 'audit',
  label: {
    fr: 'Actions traçables',
    en: 'Auditable actions'
  }
}];
const OPERATIONAL_FLOW: {
  key: string;
  label: Bi;
}[] = [{
  key: 'owner',
  label: {
    fr: 'Propriétaire vérifié',
    en: 'Verified owner'
  }
}, {
  key: 'property',
  label: {
    fr: 'Propriété',
    en: 'Property'
  }
}, {
  key: 'meter',
  label: {
    fr: 'Compteur d’eau',
    en: 'Water meter'
  }
}, {
  key: 'consumption',
  label: {
    fr: 'Données de consommation',
    en: 'Consumption data'
  }
}, {
  key: 'detection',
  label: {
    fr: 'Détection ATNA',
    en: 'ATNA detection'
  }
}, {
  key: 'notification',
  label: {
    fr: 'Notification au propriétaire',
    en: 'Owner notification'
  }
}, {
  key: 'request',
  label: {
    fr: 'Création d’une requête',
    en: 'Request creation'
  }
}, {
  key: 'resolution',
  label: {
    fr: 'Résolution',
    en: 'Resolution'
  }
}];
export function Casselman() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (b: Bi) => isFR ? b.fr : b.en;
  useEffect(() => {
    track('Case Study Viewed', {
      page: 'Town of Casselman'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-8281" data-mp="1bolacvo0owzo">
      <Header data-id="element-8282" data-mp="1bm8klwc0pic2" />

      {/* Breadcrumb bar */}
      <div className="fixed top-[65px] left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-[#E4E6EC]" data-id="element-8283" data-mp="2i56so3p7drt1">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between" data-id="element-8284" data-mp="2n26ycdi54sjm">
          <nav aria-label={isFR ? 'Fil d’Ariane' : 'Breadcrumb'} className="flex items-center gap-2 font-sans text-sm" data-id="element-8285" data-mp="bf3fp1pohxrt">
            <Link to="/" className="text-[#121212]/50 hover:text-[#121212] transition-colors" data-id="element-8286" data-mp="6hk3e7cjjv2i">
              {isFR ? 'Accueil' : 'Home'}
            </Link>
            <ChevronRightIcon className="w-3.5 h-3.5 text-[#121212]/30" data-id="element-8287" data-mp="3swwhil224zhm" />
            <Link to="/smart-water" className="text-[#121212]/50 hover:text-[#121212] transition-colors" data-id="element-8288" data-mp="3susbnxkllqep">
              {isFR ? 'Compteurs d’eau ATNA' : 'ATNA Smart Water'}
            </Link>
            <ChevronRightIcon className="w-3.5 h-3.5 text-[#121212]/30" data-id="element-8289" data-mp="3jy19r4jd7hc7" />
            <span className="text-[#121212] font-medium" data-id="element-8290" data-mp="3exdiqiv4z0hz">
              {isFR ? 'Municipalité de Casselman' : 'Municipality of Casselman'}
            </span>
          </nav>
          <Link to="/smart-water" className="font-sans text-sm text-[#121212]/50 hover:text-[#121212] transition-colors" data-id="element-8291" data-mp="1d5qpeanrutic">
            {isFR ? 'Retour aux Compteurs d’eau ATNA' : 'Back to ATNA Smart Water'}
          </Link>
        </div>
      </div>

      <main className="pt-[120px]" data-id="element-8292" data-mp="1g50k8fyvqsq1">
        {/* HERO */}
        <section className="pt-24 pb-24 px-6" data-id="element-8293" data-mp="1l555ejaqgbml">
          <div className="max-w-4xl mx-auto text-center" data-id="element-8294" data-mp="1l3u1mjhaf3vh">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="font-sans text-xs font-semibold tracking-[0.25em] text-indigo uppercase mb-6" data-id="element-8295" data-mp="1684902hljshs">
              {isFR ? 'Étude de cas · Environnement en production' : 'Case study · Live production environment'}
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.1
          }} className="font-serif text-5xl md:text-6xl text-[#121212] mb-6" data-id="element-8296" data-mp="ngl8c2b1oqix">
              {isFR ? 'Municipalité de Casselman' : 'Municipality of Casselman'}
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="font-sans text-xl text-[#121212]/60" data-id="element-8297" data-mp="3w29rbcmjfq9g">
              {isFR ? 'Compteurs d’eau ATNA en production.' : 'ATNA Smart Water in production.'}
            </motion.p>
          </div>
        </section>

        {/* RESEARCH-BACKED OUTCOMES */}
        <section className="py-28 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8298" data-mp="duaifmy1lvco">
          <div className="max-w-5xl mx-auto" data-id="element-8299" data-mp="rl5o6qd8vwc1">
            <motion.h2 {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] text-center mb-16" data-id="element-8300" data-mp="ro1im4pvw2vq">
              {isFR ? 'Résultats appuyés par la recherche.' : 'Research-backed outcomes.'}
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E6EC] border border-[#E4E6EC] mb-6" data-id="element-8301" data-mp="wltlfts2h9ep">
              {OUTCOMES.map(card => <div key={card.key} className="bg-white p-10 text-center" data-id="element-8302" data-mp="3qeumuiqtsgf2">
                  <div className="font-serif text-5xl text-[#121212] mb-3" data-id="element-8303" data-mp="3ue3ijt5feod9">
                    {pick(card.stat)}
                  </div>
                  <div className="font-sans text-sm font-semibold tracking-wide text-indigo uppercase" data-id="element-8304" data-mp="37932q9z5hcn">
                    {pick(card.label)}
                  </div>
                </div>)}
            </div>
            <p className="font-sans text-xs text-[#121212]/45 leading-relaxed max-w-3xl mx-auto text-center" data-id="element-8305" data-mp="d0hj8065zn42">
              {isFR ? '* Résultats issus d’études externes sur les compteurs intelligents, la détection des fuites et la rétroaction de consommation. Les résultats varient selon le déploiement, l’infrastructure et la participation des usagers.' : '* Findings from external studies on smart meters, leak detection and consumption feedback. Results vary by deployment, infrastructure and user participation.'}
            </p>
          </div>
        </section>

        {/* LIVE PRODUCTION ENVIRONMENT */}
        <section className="py-28 px-6 border-t border-[#E4E6EC]" data-id="element-8306" data-mp="2gerv55sgokqe">
          <div className="max-w-4xl mx-auto" data-id="element-8307" data-mp="2gdgql3r6z8y8">
            <motion.h2 {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] text-center mb-16" data-id="element-8308" data-mp="2bh8j8cj8kk1m">
              {isFR ? 'Environnement en production.' : 'Live production environment.'}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-12 max-w-2xl mx-auto" data-id="element-8309" data-mp="344cj633h286j">
              {ENVIRONMENT.map(item => <div key={item.key} className="flex items-center gap-3" data-id="element-8310" data-mp="2u8rhk62bf9xb">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo shrink-0" data-id="element-8311" data-mp="2q4jsu8vuxtz3" />
                  <span className="font-sans text-base text-[#121212]" data-id="element-8312" data-mp="1iskprtoc2ocz">
                    {pick(item.label)}
                  </span>
                </div>)}
            </motion.div>
          </div>
        </section>

        {/* OPERATIONAL FLOW */}
        <section className="py-28 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-8313" data-mp="3dx0wox59iczv">
          <div className="max-w-3xl mx-auto" data-id="element-8314" data-mp="3iv2hbmic6kdg">
            <motion.h2 {...reveal} className="font-serif text-3xl md:text-4xl text-[#121212] text-center mb-16" data-id="element-8315" data-mp="34uj9afdvfeba">
              {isFR ? 'Flux opérationnel.' : 'Operational flow.'}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="flex flex-col items-center" data-id="element-8316" data-mp="1crfcvg6ckag">
              {OPERATIONAL_FLOW.map((node, i, arr) => <Fragment key={node.key} data-id="element-8317" data-mp="3ntdpztwwaou3" data-mpi={i}>
                  <div className={`w-full max-w-md py-4 px-6 text-center border font-sans text-sm ${node.key === 'detection' ? 'border-indigo bg-indigo/5 text-indigo font-medium' : 'border-[#E4E6EC] bg-white text-[#121212]'}`} data-id="element-8318" data-mp="3nscclisepq97">
                    {pick(node.label)}
                  </div>
                  {i < arr.length - 1 && <ArrowDownIcon className="w-4 h-4 text-[#E4E6EC] my-2" data-id="element-8319" data-mp="p4yeu3h757e3" />}
                </Fragment>)}
            </motion.div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-8320" data-mp="ufkalsb5s7ia">
          <div className="max-w-3xl mx-auto text-center" data-id="element-8321" data-mp="187qikr45r86n">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-10" data-id="element-8322" data-mp="189bbydeqkl2x">
              {isFR ? 'Prêt à discuter de votre déploiement ?' : 'Ready to discuss your deployment?'}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} data-id="element-8323" data-mp="bhpuyvfiyxqx">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Casselman Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-indigo text-white text-base font-medium rounded-lg hover:bg-indigo/90 transition-colors" data-id="element-8324" data-mp="pi0ns40eameq">
                {isFR ? 'Demander une démo' : 'Request a demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-8325" data-mp="1vzfesik3ogsv" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-8326" data-mp="1uwqndj7l6oib" />
    </div>;
}